import { Controller } from "@hotwired/stimulus"

/**
 * Dropdown CTA controller — rich dropdown menu triggered by a pill button.
 *
 * Uses the native popover API (popover="auto") for positioning so the menu
 * is promoted to the top layer and never clipped by overflow:hidden ancestors.
 * Fallback: the popover attribute is widely supported (Chrome 114+, Safari 17+,
 * Firefox 125+); older browsers degrade to a hidden/shown absolute element via
 * the manual toggle logic below.
 *
 * Targets:
 *   trigger  — the button that opens/closes the menu
 *   menu     — the popover panel
 *   item     — each menuitem inside the panel
 */
export default class extends Controller {
  static targets = ["trigger", "menu", "item", "chevron"]

  connect() {
    this._onKeydown = this._handleKeydown.bind(this)
    this._onToggle = this._handleToggle.bind(this)

    this.menuTarget.addEventListener("toggle", this._onToggle)
  }

  disconnect() {
    this.menuTarget.removeEventListener("toggle", this._onToggle)
  }

  // -- Actions ----------------------------------------------------------------

  toggle(event) {
    event.stopPropagation()

    // Use the state captured at pointerdown (before the browser's popover
    // light-dismiss has a chance to close the menu) so clicking the trigger
    // while the flyout is open reliably closes it.
    const wasOpen = this._wasOpenOnPointerdown ?? this._isOpen()
    this._wasOpenOnPointerdown = undefined

    if (wasOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  recordOpenState() {
    this._wasOpenOnPointerdown = this._isOpen()
  }

  open() {
    // Close any other open dropdown-cta instances on the page
    document.querySelectorAll("[data-controller='dropdown-cta']").forEach((el) => {
      if (el !== this.element) {
        const ctrl = this.application.getControllerForElementAndIdentifier(el, "dropdown-cta")
        if (ctrl && ctrl._isOpen()) ctrl.close()
      }
    })

    this._positionMenu()
    this.menuTarget.showPopover()
    this.triggerTarget.setAttribute("aria-expanded", "true")
    if (this.hasChevronTarget) this.chevronTarget.style.transform = "rotate(180deg)"
    document.addEventListener("keydown", this._onKeydown)
    this._focusIndex = -1
  }

  close() {
    if (!this._isOpen()) return
    this.menuTarget.hidePopover()
    this.triggerTarget.setAttribute("aria-expanded", "false")
    if (this.hasChevronTarget) this.chevronTarget.style.transform = ""
    document.removeEventListener("keydown", this._onKeydown)
    this._clearFocus()
    this.triggerTarget.focus()
  }

  select(event) {
    // Let the link/form navigate normally; just close the menu.
    this.close()
  }

  // -- Popover toggle event ---------------------------------------------------

  _handleToggle(event) {
    if (this._measuring) return
    if (event.newState === "closed") {
      this.triggerTarget.setAttribute("aria-expanded", "false")
      if (this.hasChevronTarget) this.chevronTarget.style.transform = ""
      document.removeEventListener("keydown", this._onKeydown)
      this._clearFocus()
    }
  }

  // -- Positioning ------------------------------------------------------------

  _positionMenu() {
    const trigger = this.triggerTarget
    const menu = this.menuTarget
    const rect = trigger.getBoundingClientRect()
    const align = this.element.dataset.dropdownCtaAlign || "left"
    const gap = 8

    menu.style.position = "fixed"
    menu.style.visibility = "hidden"
    menu.style.top = "0"
    menu.style.left = "0"
    menu.style.right = "auto"
    this._measuring = true
    menu.showPopover()
    const menuRect = menu.getBoundingClientRect()
    menu.hidePopover()
    this._measuring = false
    menu.style.visibility = ""

    const spaceBelow = window.innerHeight - rect.bottom - gap
    const spaceAbove = rect.top - gap

    if (menuRect.height > spaceBelow && spaceAbove > spaceBelow) {
      menu.style.top = `${rect.top - menuRect.height - gap}px`
    } else {
      menu.style.top = `${rect.bottom + gap}px`
    }

    if (align === "right") {
      let right = window.innerWidth - rect.right
      if (right + menuRect.width > window.innerWidth) right = 0
      menu.style.left = "auto"
      menu.style.right = `${right}px`
    } else {
      let left = rect.left
      if (left + menuRect.width > window.innerWidth) {
        left = Math.max(0, window.innerWidth - menuRect.width)
      }
      menu.style.left = `${left}px`
      menu.style.right = "auto"
    }
  }

  // -- Keyboard ---------------------------------------------------------------

  _handleKeydown(event) {
    const items = this.itemTargets
    if (!items.length) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this._moveFocus(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this._moveFocus(-1)
        break
      case "Home":
        event.preventDefault()
        this._setFocus(0)
        break
      case "End":
        event.preventDefault()
        this._setFocus(items.length - 1)
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
      case "Enter":
      case " ":
        if (this._focusIndex >= 0 && this._focusIndex < items.length) {
          event.preventDefault()
          items[this._focusIndex].click()
        }
        break
    }
  }

  _moveFocus(delta) {
    const items = this.itemTargets
    const next = this._focusIndex < 0
      ? (delta > 0 ? 0 : items.length - 1)
      : (this._focusIndex + delta + items.length) % items.length
    this._setFocus(next)
  }

  _setFocus(index) {
    this._clearFocus()
    const items = this.itemTargets
    if (index >= 0 && index < items.length) {
      this._focusIndex = index
      items[index].dataset.focused = "true"
      items[index].focus()
    }
  }

  _clearFocus() {
    this.itemTargets.forEach((item) => delete item.dataset.focused)
  }

  // -- Helpers ----------------------------------------------------------------

  _isOpen() {
    try {
      return this.menuTarget.matches(":popover-open")
    } catch {
      return this.triggerTarget.getAttribute("aria-expanded") === "true"
    }
  }
}
